import React, { useState } from 'react';
import { SettingOutlined } from '@ant-design/icons';
import { Tabs, Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import ConfigurationCard from '../../../../common/configurationCard/ConfigurationCard';
import UserTenurePanel from './panels/UserTenurePanel';
import OverdraftPanel from './panels/OverdraftPanel';
import AchReturnPanel from './panels/AchReturnPanel';
import EmploymentTenurePanel from './panels/EmploymentTenurePanel';
import LimitsPanel from './panels/LimitsPanel';
import ProductOrStateRulesPanel from '../../productOrStateRules/ProductOrStateRulesPanel';
import {
    ADVANCED_BALANCE_TAB_KEYS,
    ADVANCED_BALANCE_TABS,
} from '../availableBalanceForm.constants';
import styles from '../styles/availableBalanceForm.module.scss';

const RULE_ROW_CLASS_NAMES = {
    row: styles.ruleRow,
    label: styles.ruleLabel,
    select: `${styles.select} ${styles.selectMd}`,
    input: `${styles.inlineNumberInput} ${styles.deductionField}`
};

const PERCENT_INPUT_PROPS = {
    min: 0,
    max: 100,
    suffix: <Text as={TextAs.SPAN} size={TextSizes.SMALL}>%</Text>
};

function AdvancedBalanceConfigCard({ rules, options, handlers }) {
    const [activeKey, setActiveKey] = useState(ADVANCED_BALANCE_TAB_KEYS.USER_TENURE);

    const panel = {
        [ADVANCED_BALANCE_TAB_KEYS.USER_TENURE]: (
            <UserTenurePanel
                rules={rules.userTenureRules}
                options={options.userTenureOptions}
                onAdd={() => handlers.addRule('userTenure', options.userTenureOptions[0]?.value)}
                onRemove={(id) => handlers.removeRule('userTenure', id)}
                onUpdate={(id, field, value) => handlers.updateRule('userTenure', id, field, value)}
            />
        ),
        [ADVANCED_BALANCE_TAB_KEYS.OVERDRAFT]: (
            <OverdraftPanel
                rules={rules.overdraftRules}
                options={options.overdraftOptions}
                onAdd={() => handlers.addRule('overdraft', options.overdraftOptions[0]?.value)}
                onRemove={(id) => handlers.removeRule('overdraft', id)}
                onUpdate={(id, field, value) => handlers.updateRule('overdraft', id, field, value)}
            />
        ),
        [ADVANCED_BALANCE_TAB_KEYS.ACH]: (
            <AchReturnPanel
                rules={rules.achRules}
                options={options.achOptions}
                onAdd={() => handlers.addRule('ach', options.achOptions[0]?.value)}
                onRemove={(id) => handlers.removeRule('ach', id)}
                onUpdate={(id, field, value) => handlers.updateRule('ach', id, field, value)}
            />
        ),
        [ADVANCED_BALANCE_TAB_KEYS.EMPLOYMENT_TENURE]: (
            <EmploymentTenurePanel
                rules={rules.employmentTenureRules}
                onAdd={() => handlers.addRule('employmentTenure')}
                onRemove={(id) => handlers.removeRule('employmentTenure', id)}
                onUpdate={(id, field, value) => handlers.updateRule('employmentTenure', id, field, value)}
            />
        ),
        [ADVANCED_BALANCE_TAB_KEYS.PRODUCT_TYPES]: (
            <ProductOrStateRulesPanel
                rules={rules.productTypeRules}
                matchKey="productType"
                options={options.productTypes}
                prefix="if a user has the product"
                valueLabel="their available balance threshold will be exactly"
                valueKey="value"
                inputProps={PERCENT_INPUT_PROPS}
                onAdd={() => handlers.addRule('productType')}
                onRemove={(id) => handlers.removeRule('productType', id)}
                onUpdate={(id, field, value) => handlers.updateRule('productType', id, field, value)}
                addLabel="Add a product type row"
                emptyTitle="No product type rules yet"
                emptyDescription="Add a row to tailor the available balance by the products a user holds."
                classNames={RULE_ROW_CLASS_NAMES}
            />
        ),
        [ADVANCED_BALANCE_TAB_KEYS.GEOGRAPHY]: (
            <ProductOrStateRulesPanel
                rules={rules.geographyRules}
                matchKey="states"
                options={options.states}
                prefix="if a user lives in"
                valueLabel="their available balance threshold will be exactly"
                valueKey="value"
                inputProps={PERCENT_INPUT_PROPS}
                onAdd={() => handlers.addRule('geography')}
                onRemove={(id) => handlers.removeRule('geography', id)}
                onUpdate={(id, field, value) => handlers.updateRule('geography', id, field, value)}
                addLabel="Add a state row"
                emptyTitle="No state rules yet"
                emptyDescription="Add a row to tailor the available balance by the state a user lives in."
                classNames={RULE_ROW_CLASS_NAMES}
            />
        ),
        [ADVANCED_BALANCE_TAB_KEYS.LIMITS]: (
            <LimitsPanel
                employmentRules={rules.employmentRules}
                balanceRules={rules.balanceRules}
                balanceOptions={options.balanceOptions}
                onAddEmployment={() => handlers.addRule('employment', 'full-time')}
                onAddBalance={() => handlers.addRule('balance', options.balanceOptions[0]?.value)}
                onRemoveEmployment={(id) => handlers.removeRule('employment', id)}
                onRemoveBalance={(id) => handlers.removeRule('balance', id)}
                onUpdateEmployment={(id, field, value) => handlers.updateRule('employment', id, field, value)}
                onUpdateBalance={(id, field, value) => handlers.updateRule('balance', id, field, value)}
            />
        ),
    };

    return (
        <ConfigurationCard title="Advanced Configurations" icon={<SettingOutlined />}>
            <Tabs
                activeKey={activeKey}
                onChange={setActiveKey}
                items={ADVANCED_BALANCE_TABS.map((tab) => ({ key: tab.key, label: tab.label }))}
                className={styles.advancedTabs}
            />
            {panel[activeKey]}
        </ConfigurationCard>
    );
}

export default AdvancedBalanceConfigCard;
